<script lang="ts">
	import { CollapsibleSectionNoBind, ShapeSelector } from "misc-ui"
	import { HouseSections } from "luxedo-data"
	import SidebarMaskDraw from "./Sidebar_MaskDraw.svelte"
	import { getContext } from "svelte"
	import { SidebarController } from "./SidebarController.svelte"
	import { ImageModifierController } from "../ImageModifierController.svelte"
	import { type ShapeOption } from "../../../modules/ImageEditor"
	import { DrawPath } from "luxedo-canvas/lib"

	const DEFAULT_NAME = "New Mask"

	let expandHelp = $derived(SidebarController.showDetails)

	let selectedShape: ShapeOption = $state("freedraw")
	let selectedSection: string = $state()

	let trackName: string = $state(DEFAULT_NAME)
	let controller = getContext<ImageModifierController>("controller")
	let isDrawing: boolean = $state(controller.imageEditor.editor.mode.get() instanceof DrawPath)

	async function initializeAddMaskMode() {
		isDrawing = true

		try {
			await controller.createMask(selectedShape, selectedSection)
		} catch (e) {
			console.error("Mask create cancelled", e)
		}

		isDrawing = false
		SidebarController.setView("default")
	}

	let prevSection: string
	function updateName(section: string) {
		if (!section) return
		if (trackName === DEFAULT_NAME || trackName === prevSection) {
			trackName = section
			prevSection = section
		}
	}

	$effect(() => {
		updateName(selectedSection)
	})
</script>

{#if isDrawing}
	<SidebarMaskDraw {selectedShape} />
{:else}
	<CollapsibleSectionNoBind expanded={expandHelp} onclick={SidebarController.toggleDetails}>
		{#snippet header()}
			<div class="heading">
				<h3>Select masks</h3>
				<button class="info-heading link-button small"
					>{expandHelp ? "Hide" : "More"} details</button
				>
			</div>
		{/snippet}
		{#snippet content()}
			<ol>
				<li>
					Select the section you are masking and name this mask
					<p class="info">This helps you identify and organize your masks later</p>
				</li>
				<li>
					Select your drawing method
					<p class="info">
						Choose a basic shape or use freeform drawing to click and add points manually
					</p>
				</li>
				<li>
					Create your mask by drawing on the canvas
					<p class="info">
						Click "Start Outlining" to begin, then click on the canvas to define your mask
						boundaries
					</p>
				</li>
			</ol>
		{/snippet}
	</CollapsibleSectionNoBind>
	<hr />

	<div class="select-container full-width expand">
		<label for="mask-layer-input">Section: </label>
		<select id="mask-layer-input" bind:value={selectedSection}>
			<option value={undefined}>-- Select Section --</option>
			{#each HouseSections as section}
				<option value={section}>{section}</option>
			{/each}
			<option value="other">Other</option>
		</select>
	</div>

	<div class="input-container full-width">
		<label for="name-input">Name: </label>
		<input type="text" id="name-input" bind:value={trackName} />
	</div>

	<br />
	<div class="shape-selection-row">
		<span class="label">Shape: </span>
		<ShapeSelector hiddenOptions={["circle", "heptagon"]} bind:selectedShape />
		<span class="shape-selection"
			>{`${selectedShape.charAt(0).toUpperCase()}${selectedShape.slice(1)}`}</span
		>
	</div>
	<div class="button-container">
		<button
			class="outline-button sidebar-button warn"
			onclick={() => SidebarController.setView("default")}>← Go Back</button
		>
		<button class="outline-button sidebar-button" onclick={initializeAddMaskMode}
			>Start Outlining</button
		>
	</div>
{/if}

<style>
	label,
	.label {
		font-size: var(--h3);
		color: var(--color-text);
		margin-right: 0.5rem;
	}

	.select-container,
	.input-container {
		margin-bottom: 0.25rem;
	}

	select,
	input {
		width: 100%;
	}

	.shape-selection-row {
		margin-top: 0.5rem;
		display: flex;
		flex-direction: row;
		align-items: center;
	}

	.shape-selection {
		margin-left: 0.5rem;
		color: var(--color-text);
	}
</style>
